/**
 * W1-11b (app-shell-ui): status chips at the right of the top bar (DESIGN.md §4.4, §5.13):
 *  - Problems: the worst severity's icon and the error / warning counts; opens the Problems tab.
 *  - Save state from the CLIENT-side storage health (a dead worker cannot report itself healthy): Saved,
 *    Saving..., "Not saved" (red; "storage full" on a quota error; also when a change waits for more than 2 s),
 *    Reconnecting, or "at risk" (red: the browser denied persistent storage, or less than 200 MB is free); its
 *    popover shows usage and protection and offers "Protect my charts", "Download backup now" and "Download
 *    recovery data".
 *  - "Changed since export": a dot when the chart changed after its last export (or was never exported); opens
 *    the export dialog (`file.export`).
 *  - Level chip: Guided / Full (settings.general.level).
 *  - The status items modules register for 'topbar.right' follow the chips (and stay in the bar when the chips
 *    move into the overflow menu below 1366 px).
 * Every chip pairs its colour with an icon and words.
 */

import { useEffect, useState } from 'react';
import type { StorageHealth } from '../../persist/types';
import { Icon } from '../../ui/Icon';
import { Menu } from '../../ui/Menu';
import { Popover } from '../../ui/Popover';
import { Button } from '../../ui/Button';
import type { MenuItem } from '../../ui/types';
import type { AppServices, ProblemsService } from '../registry/types';
import type { ShellUiService } from '../uiService';
import { problemsPanelId } from './shellCommands';
import { downloadBlob, errorText, extensionFor, fileStamp } from './download';
import { ErrorBoundary } from './ErrorBoundary';
import { useSettingsValue, useStore, useText, type ShellText } from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

/** A change waiting longer than this means the save is failing (persist/types StorageHealth). */
export const SAVE_STALL_MS = 2000;
/** Free browser storage below this is "low quota" (DESIGN.md §5.13: "< 200 MB"). */
export const LOW_SPACE_BYTES = 200 * 1024 * 1024;

export type SaveKind = 'saved' | 'saving' | 'error' | 'reconnecting' | 'atRisk';

/** Why saved work is at risk: the browser may clear it (persistence denied) or storage is almost full. */
export type SaveRisk = 'lowSpace' | 'notPersisted';

export interface SaveState {
  readonly kind: SaveKind;
  /** Quota exceeded (storage full). */
  readonly full: boolean;
  /** The storage risk, also while saving or failing (the chip shows it when nothing more serious happens). */
  readonly risk: SaveRisk | null;
}

/** Free storage is known and below LOW_SPACE_BYTES. */
export function lowSpace(h: StorageHealth): boolean {
  return h.quotaBytes > 0 && h.quotaBytes - h.usageBytes < LOW_SPACE_BYTES;
}

/** The storage risk of a health report (low space first: it fails soonest), or null. */
export function storageRisk(h: StorageHealth): SaveRisk | null {
  if (lowSpace(h)) return 'lowSpace';
  return h.persisted ? null : 'notPersisted';
}

/**
 * Save state from the storage health and whether a change was just committed. Write failures and reconnecting come
 * first; a storage risk keeps the chip red (DESIGN.md §5.13) while changes keep being saved.
 */
export function saveStateOf(h: StorageHealth | null, justCommitted: boolean): SaveState {
  if (h === null) return { kind: justCommitted ? 'saving' : 'saved', full: false, risk: null };
  const risk = storageRisk(h);
  if (h.lastWriteError !== null) {
    return { kind: 'error', full: /quota|full|space/i.test(h.lastWriteError), risk };
  }
  if (!h.workerAlive) return { kind: 'reconnecting', full: false, risk };
  if (h.pendingTxns > 0 && h.oldestUnackedMs > SAVE_STALL_MS) return { kind: 'error', full: false, risk };
  if (risk !== null) return { kind: 'atRisk', full: false, risk };
  if (h.pendingTxns > 0 || justCommitted) return { kind: 'saving', full: false, risk };
  return { kind: 'saved', full: false, risk };
}

/** The state needs the user (red chip, overflow attention dot). */
export function saveNeedsAttention(state: SaveState): boolean {
  return state.kind === 'error' || state.kind === 'atRisk';
}

/** Storage health from the persist client (initial read + live updates); null until known. */
export function useStorageHealth(services: AppServices): StorageHealth | null {
  const [health, setHealth] = useState<StorageHealth | null>(null);
  const { persist } = services;
  useEffect(() => {
    let alive = true;
    const off = persist.onHealth((h) => {
      if (alive) setHealth(h);
    });
    persist
      .health()
      .then((h) => {
        if (alive) setHealth((prev) => prev ?? h);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      off();
    };
  }, [persist]);
  return health;
}

/** True for a short while after each committed change (until the health reports it). */
function useJustCommitted(services: AppServices): boolean {
  const store = useStore(services);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (store === null) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = store.subscribeCommits(() => {
      setOn(true);
      clearTimeout(timer);
      timer = setTimeout(() => {
        setOn(false);
      }, 700);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
  }, [store]);
  return on;
}

export function formatBytes(n: number, text: ShellText): string {
  if (n >= 1024 ** 3) return text.t(m.bytes, { n: n / 1024 ** 3, unit: 'gb' });
  if (n >= 1024 ** 2) return text.t(m.bytes, { n: n / 1024 ** 2, unit: 'mb' });
  if (n >= 1024) return text.t(m.bytes, { n: n / 1024, unit: 'kb' });
  return text.t(m.bytes, { n, unit: 'b' });
}

export function timeAgo(at: number, now: number, text: ShellText): string {
  const sec = Math.max(0, (now - at) / 1000);
  if (sec < 45) return text.t(m.timeAgoNow);
  if (sec < 3600) return text.t(m.timeAgoMinutes, { n: Math.max(1, Math.round(sec / 60)) });
  if (sec < 86400) return text.t(m.timeAgoHours, { n: Math.round(sec / 3600) });
  return text.t(m.timeAgoDays, { n: Math.round(sec / 86400) });
}

/** Downloads the open project's recovery data (toasts on failure). */
export async function downloadProjectRecovery(services: AppServices): Promise<void> {
  const store = services.store;
  if (store === null) return;
  try {
    const blob = await services.persist.recoveryData(store.projectId);
    downloadBlob(blob, `phiedit-recovery-${store.projectId}-${fileStamp()}.${extensionFor(blob.type)}`);
  } catch (err) {
    services.ui.toast(
      { key: m.toastRecoveryFailed, params: { error: errorText(err, services.i18n.t(m.unknownError)) } },
      { kind: 'error' },
    );
  }
}

/**
 * "Download backup now" (DESIGN.md §5.13): the export dialog (`file.export`; its targets include the project backup
 * with every asset) when it exists, else the project's recovery data (snapshot and journal in one file).
 */
export function downloadBackup(services: AppServices): void {
  if (services.registry.command('file.export') !== undefined) {
    void services.registry.runAny('file.export', { quick: false }, 'button');
    return;
  }
  void downloadProjectRecovery(services);
}

/** Asks the browser for persistent storage and tells the user how it went. */
export async function protectCharts(services: AppServices): Promise<void> {
  const ok = await services.persist.requestPersistence().catch(() => false);
  services.ui.toast({ key: ok ? m.toastProtected : m.toastProtectRefused }, { kind: ok ? 'success' : 'warning' });
}

/** Icon of a save state (the risk decides the at-risk icon). */
export function saveIcon(state: SaveState): string {
  switch (state.kind) {
    case 'saved':
      return 'CloudCheck';
    case 'saving':
      return 'CloudUpload';
    case 'reconnecting':
      return 'CloudOff';
    case 'error':
      return 'CloudAlert';
    case 'atRisk':
      return state.risk === 'lowSpace' ? 'HardDrive' : 'ShieldAlert';
  }
}

export function saveLabel(state: SaveState, text: ShellText): string {
  switch (state.kind) {
    case 'saved':
      return text.t(m.saveSaved);
    case 'saving':
      return text.t(m.saveSaving);
    case 'reconnecting':
      return text.t(m.saveReconnecting);
    case 'error':
      return text.t(state.full ? m.saveErrorFull : m.saveError);
    case 'atRisk':
      return text.t(m.saveAtRisk, { risk: state.risk ?? 'notPersisted' });
  }
}

export function SaveChip(props: { readonly services: AppServices; readonly compact?: boolean }) {
  const { services } = props;
  const text = useText(services);
  const health = useStorageHealth(services);
  const just = useJustCommitted(services);
  const state = saveStateOf(health, just);
  const label = saveLabel(state, text);
  const [now, setNow] = useState(() => Date.now());
  return (
    <Popover
      label={text.t(m.saveDetailsTitle)}
      align="end"
      onOpenChange={(open) => {
        if (open) setNow(Date.now());
      }}
      trigger={
        <button type="button" className={s.chip} data-state={state.kind} aria-label={label} title={label}>
          <Icon name={saveIcon(state)} size={16} />
          {saveNeedsAttention(state) && props.compact !== true && <span className={s.chipText}>{label}</span>}
        </button>
      }
    >
      <div className={s.chipDetails}>
        <p className={s.chipDetailsTitle}>{label}</p>
        {health === null || health.lastFlushAt === 0 ? (
          <p>{text.t(m.saveNeverSaved)}</p>
        ) : (
          <p>{text.t(m.saveLastSaved, { time: timeAgo(health.lastFlushAt, now, text) })}</p>
        )}
        {health !== null && health.pendingTxns > 0 && <p>{text.t(m.savePending, { count: health.pendingTxns })}</p>}
        {health?.lastWriteError != null && (
          <p className={s.chipError}>{text.t(m.saveWriteError, { error: health.lastWriteError })}</p>
        )}
        {health !== null && health.quotaBytes > 0 && (
          <p className={lowSpace(health) ? s.chipError : undefined}>
            {text.t(m.saveUsage, {
              used: formatBytes(health.usageBytes, text),
              quota: formatBytes(health.quotaBytes, text),
              free: formatBytes(Math.max(0, health.quotaBytes - health.usageBytes), text),
            })}
          </p>
        )}
        {health !== null && (
          <p className={health.persisted ? undefined : s.chipError}>
            {text.t(health.persisted ? m.saveProtected : m.saveNotProtected)}
          </p>
        )}
        <div className={s.chipActions}>
          {health !== null && !health.persisted && (
            <Button
              size="sm"
              variant="secondary"
              icon="ShieldCheck"
              onClick={() => {
                void protectCharts(services);
              }}
            >
              {text.t(m.protectCharts)}
            </Button>
          )}
          <Button
            size="sm"
            variant={saveNeedsAttention(state) ? 'primary' : 'secondary'}
            icon="HardDriveDownload"
            onClick={() => {
              downloadBackup(services);
            }}
          >
            {text.t(m.downloadBackup)}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon="Download"
            onClick={() => {
              void downloadProjectRecovery(services);
            }}
          >
            {text.t(m.downloadRecovery)}
          </Button>
        </div>
      </div>
    </Popover>
  );
}

export interface ProblemCounts {
  readonly fatal: number;
  readonly error: number;
  readonly warning: number;
  readonly tip: number;
}

export function useProblemCounts(problems: ProblemsService): ProblemCounts {
  const [counts, setCounts] = useState<ProblemCounts>(() => problems.counts());
  useEffect(() => {
    const update = (): void => {
      const next = problems.counts();
      setCounts((prev) =>
        prev.fatal === next.fatal && prev.error === next.error && prev.warning === next.warning && prev.tip === next.tip
          ? prev
          : next,
      );
    };
    update();
    return problems.subscribe(update);
  }, [problems]);
  return counts;
}

/** How long a project may stay without any check pass before the chip stops saying "Checking" (inert checkers). */
export const FIRST_PASS_WAIT_MS = 1500;

/**
 * True until the first check pass of the open chart has finished (UX-014): the counts mean nothing before it. A pass
 * is seen as finished when `checking()` turns false after having been true, when problems arrive, or when no pass
 * starts within FIRST_PASS_WAIT_MS (a checker that never runs).
 */
export function useFirstPassPending(services: AppServices): boolean {
  const store = useStore(services);
  const problems = services.problems;
  const project = store?.projectId ?? null;
  // The project whose first pass has finished (set from the checker's notifications and a timer only).
  const [finishedFor, setFinishedFor] = useState<string | null>(null);
  useEffect(() => {
    if (project === null) return undefined;
    let sawRunning = false;
    const finish = (): void => {
      setFinishedFor(project);
    };
    const check = (): void => {
      const c = problems.counts();
      if (c.fatal + c.error + c.warning + c.tip > 0) finish();
      else if (problems.checking()) sawRunning = true;
      else if (sawRunning) finish();
    };
    // The state at subscription time is read in a microtask (no state update in the effect body).
    queueMicrotask(check);
    const timer = setTimeout(() => {
      if (!sawRunning && !problems.checking()) finish();
    }, FIRST_PASS_WAIT_MS);
    const off = problems.subscribe(check);
    return () => {
      clearTimeout(timer);
      off();
    };
  }, [problems, project]);
  return project !== null && finishedFor !== project;
}

export function ProblemsChip(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const { services, ui } = props;
  const text = useText(services);
  const c = useProblemCounts(services.problems);
  const pending = useFirstPassPending(services);
  const panel = problemsPanelId(services);
  const errors = c.fatal + c.error;
  const total = errors + c.warning + c.tip;
  const label = pending
    ? text.t(m.problemsChecking)
    : total === 0
      ? text.t(m.problemsNone)
      : text.t(m.problemsChip, { fatal: c.fatal, errors: c.error, warnings: c.warning, tips: c.tip });
  return (
    <button
      type="button"
      className={s.chip}
      data-severity={
        pending ? 'checking' : c.fatal > 0 ? 'fatal' : c.error > 0 ? 'error' : c.warning > 0 ? 'warning' : 'none'
      }
      data-help-id="problems.chip"
      aria-label={label}
      title={label}
      aria-busy={pending ? true : undefined}
      aria-disabled={panel === null ? true : undefined}
      onClick={() => {
        if (panel !== null) ui.showPanel(panel);
      }}
    >
      {pending ? (
        <>
          <span className={s.chipSpinner} aria-hidden="true">
            <Icon name="LoaderCircle" size={16} />
          </span>
          <span className={s.chipText}>{text.t(m.problemsCheckingShort)}</span>
        </>
      ) : (
        <>
          <Icon name={c.fatal > 0 ? 'OctagonX' : 'CircleX'} size={16} />
          <span className={s.chipCount}>{errors}</span>
          <Icon name="TriangleAlert" size={16} />
          <span className={s.chipCount}>{c.warning}</span>
        </>
      )}
    </button>
  );
}

export interface ExportState {
  /** Changes after the last export (or never exported). */
  readonly changed: boolean;
  /** "Changed since the last export (2 hours ago). Click to export." */
  readonly label: string;
  /** The export dialog exists (`file.export`). */
  readonly canExport: boolean;
}

/** Whether the open chart changed since its last export, with the chip's words; null without a project. */
export function useExportState(services: AppServices): ExportState | null {
  const text = useText(services);
  const store = useStore(services);
  // "now" for the relative time; refreshed by every commit and once a minute.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 60_000);
    const off =
      store?.subscribeCommits(() => {
        setNow(Date.now());
      }) ?? null;
    return () => {
      clearInterval(timer);
      off?.();
    };
  }, [store]);
  if (store === null) return null;
  const last = store.doc.editor.lastExport;
  const changed = last === null ? store.seq > 0 : store.seq > last.seq;
  const label = last === null ? text.t(m.exportNever) : text.t(m.exportChanged, { when: timeAgo(last.at, now, text) });
  return { changed, label, canExport: services.registry.command('file.export') !== undefined };
}

/** Opens the export dialog (no-op when this version has none). */
export function openExport(services: AppServices): void {
  if (services.registry.command('file.export') !== undefined) {
    void services.registry.runAny('file.export', { quick: false }, 'button');
  }
}

export function ExportChip(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const state = useExportState(services);
  if (state === null || !state.changed) return null;
  return (
    <button
      type="button"
      className={s.chip}
      data-state="changed"
      aria-label={state.label}
      title={state.label}
      aria-disabled={state.canExport ? undefined : true}
      onClick={() => {
        openExport(services);
      }}
    >
      <Icon name="CircleDot" size={14} />
      <span className={s.srOnly}>{text.t(m.exportChip)}</span>
    </button>
  );
}

export function LevelChip(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const level = useSettingsValue(services).general.level;
  const items: MenuItem[] = (['guided', 'full'] as const).map((l) => ({
    kind: 'action',
    label: (
      <span className={s.levelOption}>
        <span>{text.t(l === 'guided' ? m.levelGuided : m.levelFull)}</span>
        <span className={s.levelDetail}>{text.t(l === 'guided' ? m.levelGuidedDetail : m.levelFullDetail)}</span>
      </span>
    ),
    ...(l === level ? { icon: 'Check' } : {}),
    onSelect: () => {
      services.settings.set('general', 'level', l);
      services.editor.getState().setLevel(l);
    },
  }));
  return (
    <Menu
      align="end"
      label={text.t(m.levelLabel, { level })}
      trigger={
        <button type="button" className={s.levelChip} aria-label={text.t(m.levelLabel, { level })}>
          {text.t(level === 'guided' ? m.levelGuided : m.levelFull)}
          <Icon name="ChevronDown" size={12} />
        </button>
      }
      items={items}
    />
  );
}

/**
 * The status items other modules register for 'topbar.right', each in its own silent error boundary. They stay in
 * the bar when the shell's chips move into the overflow menu (a component cannot become a menu row).
 */
export function ModuleStatusItems(props: { readonly services: AppServices; readonly className?: string }) {
  const { services } = props;
  const items = services.registry.statusItems('topbar.right');
  if (items.length === 0) return null;
  return (
    <div className={props.className ?? s.chips} data-status-items="">
      {items.map((item) => (
        <ErrorBoundary key={item.id} variant="silent" services={services} label={item.id}>
          <item.component services={services} />
        </ErrorBoundary>
      ))}
    </div>
  );
}

export function StatusChips(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  /** Narrow bar: the save chip keeps only its icon (the words are in its tooltip and popover). */
  readonly compact?: boolean;
}) {
  const { services, ui } = props;
  return (
    <div className={s.chips}>
      <ProblemsChip services={services} ui={ui} />
      <SaveChip services={services} compact={props.compact === true} />
      <ExportChip services={services} />
      <ModuleStatusItems services={services} />
    </div>
  );
}
